/**
 * Encodes/decodes a QueryBuilderSubquery for use in URL query parameters
 * (e.g. inventory filters).
 *
 * Version handling mirrors `loadedState/encodeDecodeState.ts`:
 * - All previously released versions are kept in `decoderVersionMap` so older
 *   URLs still decode.
 * - A single import aliased as `currentVersionEncoder` decides which version
 *   new URLs are written with.
 *
 * To roll a new version:
 * - Add the version key to `SUPPORTED_VERSIONS`.
 * - Add a decoder entry to `decoderVersionMap` (typically a thin wrapper around
 *   the corresponding `encodeDecode.vX` module).
 * - Flip the `currentVersionEncoder` / `getAllTokens` / `getTokenIndexAndTokens`
 *   / `getEncodedTokens` imports to the new version and bump `CURRENT_VERSION`.
 */

import { QueryBuilderSubquery } from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { STATE_SEPARATOR } from './loadedState/consts';
import {
  getAllTokens,
  encodeSearchQuery as currentVersionEncoder,
  getEncodedTokens,
  getTokenIndexAndTokens,
} from './loadedState/encodeDecode.v9/encode';
import {
  decodeSearchQuery as decodeSearchQueryV9,
  getTokenIndexFromEncodedTokens as getTokenIndexFromEncodedTokensV9,
} from './loadedState/encodeDecode.v9/decode';
import { splitVersionAndState } from './loadedState/encodeDecodeState';

const SUPPORTED_VERSIONS = ['v1'] as const;
type SubquerySupportedVersions = (typeof SUPPORTED_VERSIONS)[number];

const isSupportedVersion = (
  version: string
): version is SubquerySupportedVersions =>
  SUPPORTED_VERSIONS.includes(version as SubquerySupportedVersions);

const CURRENT_VERSION: SubquerySupportedVersions = 'v1';

type Decoder = (state: string) => QueryBuilderSubquery;

const makeDecoder =
  (
    decodeSearchQuery: typeof decodeSearchQueryV9,
    getTokenIndexFromEncodedTokens: typeof getTokenIndexFromEncodedTokensV9
  ): Decoder =>
  state => {
    const [encodedQuery, encodedTokens] = state.split(STATE_SEPARATOR);
    const hexIndexToToken = getTokenIndexFromEncodedTokens(encodedTokens);
    // decodeSearchQuery is typed to return QueryBuilderQuery, but we encoded a
    // plain QueryBuilderSubquery (no SearchContextRule wrapper), so decodeRules
    // returns [subquery] and [0] is our subquery. The cast corrects the type
    // back to what was actually encoded.
    return decodeSearchQuery(
      encodedQuery,
      hexIndexToToken
    ) as unknown as QueryBuilderSubquery;
  };

const decoderVersionMap: Record<SubquerySupportedVersions, Decoder> = {
  v1: makeDecoder(decodeSearchQueryV9, getTokenIndexFromEncodedTokensV9),
};

export const encodeSubquery = (subquery: QueryBuilderSubquery): string => {
  try {
    const tokens = getAllTokens(subquery);
    const { tokenToHexIndex, tokensWithoutDefaults } =
      getTokenIndexAndTokens(tokens);
    const encodedTokens = getEncodedTokens(tokensWithoutDefaults);
    return [
      CURRENT_VERSION,
      currentVersionEncoder(subquery, tokenToHexIndex),
      encodedTokens,
    ].join(STATE_SEPARATOR);
  } catch {
    logError(Error('Encoding subquery failed'));
    return '';
  }
};

export const decodeSubquery = (
  encoded: string | null
): QueryBuilderSubquery | null => {
  if (!encoded) return null;
  try {
    const { version, state } = splitVersionAndState(encoded);
    if (!isSupportedVersion(version)) {
      logError(Error('Unhandled version in encoded subquery'));
      return null;
    }
    return decoderVersionMap[version](state);
  } catch {
    logError(Error('Decoding subquery failed'));
    return null;
  }
};
